import {ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {Order, Product, Risk, StandardName} from "../../../../store/state/order.state";
import {ChangeSet} from "../../history/order-history.model";
import {BaseSubFormComponent} from "../../../../umbrella-common/components/base-sub-form.component";
import {PriceAdjustmentsFormService} from "./price-adjustments-form.service";
import {FormGroup} from "@angular/forms";
import {Observable, Subscription} from "rxjs";
import {ConfigurationProvider} from "../../../../configuration/configuration.provider";
import {filter, first, map, mergeMap, tap} from "rxjs/operators";
import {FeeConfig} from "../../../../configuration/configuration.model";

@Component({
    selector: 'pricing-adjustments',
    templateUrl: './pricing-adjustments.template.html',
    styleUrls: ['./pricing-adjustments.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PricingAdjustmentsComponent extends BaseSubFormComponent implements OnInit, OnDestroy, OnChanges {

	@Input() order: Order;
	@Input() product: Product;
	@Input() changeSet: ChangeSet;
	@Input() intermediatePriceAdjustments: IntermediatePriceAdjustment[] = [];
	@Input() priceAdjustmentsConfig: PriceAdjustmentsConfig;
	@Input() hackForCertificationFee: boolean = false; // https://jira.dekra-glass.com/browse/UMB-4805 bug#8

	config$: Observable<PriceAdjustmentsConfig>;
	feesRateHint$: Observable<string>;
	form: FormGroup;
	subscriptions: Subscription[] = [];
	currency: string;
	intermediatePriceAdjustmentsSorted: IntermediatePriceAdjustment[];
	priceAdjustmentsFormService: PriceAdjustmentsFormService;

	constructor(private formService: PriceAdjustmentsFormService, private countryConfig: ConfigurationProvider) {
		super(formService);
		this.priceAdjustmentsFormService = formService;
	}

	ngOnInit(): void {
		this.config$ = this.countryConfig.getConfigurationFor(this.order.clientCountry).pipe(
			first(),
			tap(config => {
				this.currency = config.clientCountryConfig.order.pricing.currency.symbol;
			}),
			map(config => {
				return Object.assign({
					hackForCertificationFee: this.hackForCertificationFee
				} as PriceAdjustmentsConfig, config.clientCountryConfig?.pricingAdjustments);
			}),
			map(config => {
				return Object.assign(config, this.priceAdjustmentsConfig == null ? {}: this.priceAdjustmentsConfig);
			}));

		this.config$.subscribe(config => this.form = this.formService.buildSubForm(this.product.standard.name, this.totalSubForms(this.order), this.product, this.intermediatePriceAdjustments, config));
		this.feesRateHint$ = this.config$.pipe(map(config => `from ${config.dailyRate.minAmount} to ${config.dailyRate.maxAmount} ${this.currency}`));

		this.intermediatePriceAdjustmentsSorted = this.intermediatePriceAdjustments.sort((adj1, adj2) => adj1.type.localeCompare(adj2.type));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['product'] && !changes['product'].firstChange) {
			this.formService.updateCurrentProduct(this.product);
		}
		if (changes['showCertFee'] && changes['showCertFee'].previousValue == null && changes['showCertFee'].currentValue) {
			this.config$.pipe(first()).subscribe(config => this.formService.buildFeesControls(this.product, this.form,  config));
		}
	}

	override ngOnDestroy(): void {
		this.subscriptions.forEach(sub => sub.unsubscribe());
		this.formService.destroySubForm(this.product.standard.name);
	}

	getChosenValueFor(intermediatePriceAdjustment: IntermediatePriceAdjustment): Risk | null {
		switch (intermediatePriceAdjustment.type) {
			case IntermediatePriceAdjustmentType.RISK:
				return this.product.pricing.pricingAdjustments.risk != null && this.product.pricing.pricingAdjustments.risk != Risk.NO_RISK ? this.product.pricing.pricingAdjustments.risk : this.product.pricing.risk;
			case IntermediatePriceAdjustmentType.IT_COMPLEXITY:
				return this.product.pricing.pricingAdjustments.itComplexity != null ? this.product.pricing.pricingAdjustments.itComplexity : this.product.pricing.itComplexity
			case IntermediatePriceAdjustmentType.BUSINESS_COMPLEXITY:
				return this.product.pricing.pricingAdjustments.businessComplexity != null ? this.product.pricing.pricingAdjustments.businessComplexity : this.product.pricing.businessComplexity;
			case IntermediatePriceAdjustmentType.AUDIT_EFFORT:
				return this.product.pricing.pricingAdjustments.auditEffort != null ? this.product.pricing.pricingAdjustments.auditEffort : this.product.pricing.auditEffort;
			case IntermediatePriceAdjustmentType.COMPLEXITY_FACTOR:
				return this.product.pricing.pricingAdjustments.complexityFactor != null ? this.product.pricing.pricingAdjustments.complexityFactor : this.product.pricing.complexityFactor;
			default:
				return null;
		}
	}

	standard(): StandardName {
		return this.product.standard.name;
	}

	getFormKey(): string {
		return this.standard();
	}

	getControlNameFor(intermediatePriceAdjustment: IntermediatePriceAdjustment): string {
		return this.formService.getControlNameFor(intermediatePriceAdjustment, this.standard());
	}

	dailyRateFormControl(): string {
		return this.formService.dailyRateFormControl(this.standard());
	}

	feesModeControl(): string {
		return this.formService.feeFormControl(this.standard(), 'fee-modes');
	}

	getRadioGroupName(intermediatePriceAdjustment: IntermediatePriceAdjustment) {
		return `${this.product.standard.name}${intermediatePriceAdjustment.type}`;
	}

	getRadioBtnName(intermediatePriceAdjustment: IntermediatePriceAdjustment, adjustmentValue: Risk) {
		return `${this.product.standard.name}${intermediatePriceAdjustment.type}${adjustmentValue}`;
	}

	protected iso27701HasItsOwnSubForm(): boolean {
		return true;
	}

	isTransfer(): boolean {
		const transferQuestions = this.product.transferQuestions;
		return transferQuestions?.lastAuditEvent !== null;
	}
}

export interface PriceAdjustmentsConfig {
	currency: string;
	hackForCertificationFee: boolean;
	dailyRate: {
		minAmount: number;
		maxAmount: number;
	},
	fees: FeeConfig[],
}

export interface IntermediatePriceAdjustment {
	type: IntermediatePriceAdjustmentType;
	standards: StandardName[];
	values: Risk[];
}

export enum IntermediatePriceAdjustmentType {
	RISK = 'RISK',
	IT_COMPLEXITY = 'IT_COMPLEXITY',
	BUSINESS_COMPLEXITY = 'BUSINESS_COMPLEXITY',
	AUDIT_EFFORT = 'AUDIT_EFFORT',
	COMPLEXITY_FACTOR = 'COMPLEXITY_FACTOR'
}
